import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  restrictedAccessAdminService,
} from '../../services/RestrictedAccessAdminService';

interface RestrictedAccessAdminStatus {
  isError: boolean;
  message: string;
}

interface UseRestrictedAccessAdminSettingsResult {
  enabled: boolean;
  saving: boolean;
  loading: boolean;
  status: RestrictedAccessAdminStatus | null;
  handleEnabledChange: (event: ChangeEvent<HTMLInputElement>) => void;
  saveSettings: () => Promise<void>;
}

const readErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallbackMessage;
};

export const useRestrictedAccessAdminSettings = (): UseRestrictedAccessAdminSettingsResult => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<RestrictedAccessAdminStatus | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const settings = await restrictedAccessAdminService.getSettings();
        if (!cancelled) {
          setEnabled(settings.enabled);
        }
      } catch (error) {
        if (!cancelled) {
          setStatus({
            isError: true,
            message: readErrorMessage(error, 'Impossible de charger le reglage du formulaire.'),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnabledChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEnabled(event.target.checked);
  }, []);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    setStatus(null);

    try {
      const updated = await restrictedAccessAdminService.updateSettings(enabled);
      setEnabled(updated.enabled);
      setStatus({
        isError: false,
        message: updated.enabled
          ? 'Le formulaire de lien de connexion est active.'
          : 'Le formulaire de lien de connexion est desactive.',
      });
    } catch (error) {
      setStatus({
        isError: true,
        message: readErrorMessage(error, 'Impossible de mettre a jour le reglage du formulaire.'),
      });
    } finally {
      setSaving(false);
    }
  }, [enabled]);

  return {
    enabled,
    saving,
    loading,
    status,
    handleEnabledChange,
    saveSettings,
  };
};

export default useRestrictedAccessAdminSettings;
